import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function ManchesterPage() {
  return (
    <>
      <LocalBusinessSchema city="Manchester" latitude={53.4808} longitude={-2.2426} />
      <PageHero
        title="Fractional CISO Services in Manchester"
        subtitle="Cybersecurity leadership for the North's digital powerhouse"
        eyebrow="Manchester"
        seoTitle="Fractional CISO Manchester | The CISO Network"
        seoDescription="Expert fractional CISO services for Manchester businesses. Access senior cybersecurity leadership for your Northern tech company."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing Manchester's Digital Revolution
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Manchester has transformed into one of Europe's most exciting digital cities, with
                  a tech ecosystem spanning fintech, e-commerce, media, and advanced manufacturing.
                  From MediaCityUK to the Northern Quarter, the city buzzes with innovative
                  businesses that are reshaping their industries through technology.
                </p>
                <p>
                  The North West's business landscape presents unique cybersecurity challenges.
                  Media and broadcasting companies handle valuable content and face targeted
                  attacks. E-commerce firms process thousands of transactions daily requiring
                  PCI-DSS compliance. Meanwhile, the region's booming fintech sector must meet
                  exacting regulatory standards while moving at startup speed.
                </p>
                <p>
                  Despite Manchester's thriving tech scene, access to senior cybersecurity talent
                  remains a challenge. Many businesses can't justify the cost of a full-time CISO,
                  yet they face the same threats as London enterprises. Our fractional CISO service
                  brings executive-level security expertise to Manchester businesses on terms that
                  work for them.
                </p>
                <p>
                  Manchester's economy spans media, technology, e-commerce, and professional
                  services, with MediaCity and the Northern Quarter hosting fast-growing digital
                  businesses. These firms face increasing customer security questionnaires, investor
                  due diligence, and regulatory attention. A fractional CISO helps Manchester
                  businesses compete with London rivals on security credibility.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique dynamics of Northern businesses,
                      from media to fintech to advanced manufacturing.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Northern Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Greater Manchester, Liverpool, Leeds, and the
                      wider North West and Yorkshire regions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Northern Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              World-class security leadership without the London price tag
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Media Security',
                description:
                  'Protect valuable content and broadcasting infrastructure from targeted attacks.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description: 'Access senior expertise without the premium costs of London CISOs.',
              },
              {
                icon: Users,
                title: 'Sector Knowledge',
                description:
                  'CISOs with experience in media, fintech, e-commerce, and manufacturing.',
              },
              {
                icon: Clock,
                title: 'Agile Support',
                description: 'Flexible engagement that scales with your Manchester business.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Manchester - FAQs"
        subtitle="Common questions from Manchester and North West businesses"
        items={[
          {
            id: 'manchester-1',
            question: 'How does a fractional CISO work with businesses in Manchester?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Manchester. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing media, technology, and e-commerce companies.',
          },
          {
            id: 'manchester-2',
            question: 'What size of business benefits from a fractional CISO in Manchester?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Manchester startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'manchester-3',
            question: 'What industries do your CISOs cover in Manchester?',
            answer:
              "Our network includes CISOs with experience in media, technology, and e-commerce. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'manchester-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'manchester-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Manchester Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can accelerate your growth
            securely.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
